import { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Inbox, RefreshCcw, ArrowLeft, Lock, LogOut, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mrl_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [verifying, setVerifying] = useState(!!token);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  // Verify saved token on mount
  useEffect(() => {
    if (!token) return;
    let active = true;
    (async () => {
      try {
        await axios.get(`${API}/admin/verify`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) setVerifying(false);
      } catch {
        if (active) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setVerifying(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const loadInquiries = async (t = token) => {
    if (!t) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inquiries`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setInquiries(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Session expired", { description: "Please log in again." });
      } else {
        toast.error("Failed to load inquiries");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && !verifying) loadInquiries(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, verifying]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      /* ignore */
    }
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setInquiries([]);
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-muted/40 grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!token) {
    return (
      <LoginGate
        onSuccess={(t) => {
          localStorage.setItem(TOKEN_KEY, t);
          setToken(t);
        }}
      />
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-muted/40">
      <div className="border-b border-border bg-white">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="back-home-link"
              className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
            <div>
              <h1 className="font-heading text-2xl uppercase tracking-tight">
                Inquiries Dashboard
              </h1>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">
                MRL Plant Hire · Internal
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="refresh-inquiries-btn"
              onClick={() => loadInquiries()}
              variant="outline"
              className="btn-sharp"
            >
              <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="btn-sharp"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {loading ? (
          <p data-testid="inquiries-loading" className="text-muted-foreground">
            Loading…
          </p>
        ) : inquiries.length === 0 ? (
          <Card
            data-testid="no-inquiries"
            className="rounded-sm border border-dashed p-12 text-center bg-white"
          >
            <Inbox className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <h3 className="font-heading uppercase text-xl">No inquiries yet</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Booking requests submitted from the website will appear here.
            </p>
          </Card>
        ) : (
          <div data-testid="inquiries-list" className="grid gap-4">
            {inquiries.map((inq) => (
              <Card
                key={inq.id}
                data-testid={`inquiry-${inq.id}`}
                className="rounded-sm border border-border bg-white p-6"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-heading text-lg uppercase">
                      {inq.equipment_name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {inq.start_date} → {inq.end_date}
                    </p>
                  </div>
                  <Badge className="rounded-sm bg-orange-500 hover:bg-orange-500 text-white uppercase">
                    {inq.status}
                  </Badge>
                </div>
                <div className="grid md:grid-cols-3 gap-4 mt-4 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">
                      Contact
                    </div>
                    <div className="font-medium">{inq.name}</div>
                    <div>{inq.email}</div>
                    <div>{inq.phone}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">
                      Delivery
                    </div>
                    <div>{inq.delivery_address || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">
                      Notes
                    </div>
                    <div className="whitespace-pre-wrap">{inq.notes || "—"}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LoginGate({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      onSuccess(res.data.token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-gate"
      className="min-h-screen bg-muted/40 grid place-items-center px-6"
    >
      <Card className="w-full max-w-md rounded-sm border border-border bg-white p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-secondary text-orange-400 grid place-items-center">
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <h1 className="font-heading uppercase text-xl tracking-tight">
              Admin Access
            </h1>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              MRL Plant Hire
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4" data-testid="admin-login-form">
          <div className="grid gap-1.5">
            <Label className="font-heading uppercase tracking-wider text-xs text-secondary">
              Password
            </Label>
            <Input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              placeholder="Enter admin password"
              className="rounded-sm"
            />
            {error && (
              <span data-testid="admin-login-error" className="text-xs text-red-600">
                {error}
              </span>
            )}
          </div>
          <Button
            type="submit"
            disabled={submitting || !password}
            data-testid="admin-login-btn"
            className="btn-sharp bg-orange-500 hover:bg-orange-600 text-white font-heading uppercase tracking-wider"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in…
              </>
            ) : (
              "Sign In"
            )}
          </Button>
          <Link
            to="/"
            className="text-xs text-muted-foreground hover:text-foreground text-center"
          >
            ← Back to site
          </Link>
        </form>
      </Card>
    </div>
  );
}
